export const MAX_FILE_MB = 50

const MAX_BYTES = MAX_FILE_MB * 1024 * 1024

export const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.ods'] as const

export const ACCEPTED_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.oasis.opendocument.spreadsheet',
] as const

export const ACCEPT_ATTRIBUTE = [
  ...ACCEPTED_EXTENSIONS,
  ...ACCEPTED_MIME_TYPES,
].join(',')

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_BYTES
}

export function convertedFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}.pdf`
}

// ──────── Preflight ──────────────────────────────────────────────────────────
//
// The renderer draws every PDF page on a canvas, so the workbook is measured
// first: huge sheets are rejected before they turn into thousands of pages.

export interface XlsxClassification {
  sheetCount: number
  /** Row count of the largest sheet. */
  maxRows: number
  /** Column count of the widest row in the largest sheet. */
  maxCols: number
  /** Largest sheet's `rows × cols` product. */
  maxCells: number
  /** Row count across all sheets. */
  totalRows: number
}

export async function classifyXlsx(file: File): Promise<XlsxClassification> {
  const XLSX = await import('xlsx')
  const buf = new Uint8Array(await file.arrayBuffer())
  const wb = XLSX.read(buf, { type: 'array', cellFormula: false, cellStyles: false })

  let maxRows = 0
  let maxCols = 0
  let maxCells = 0
  let totalRows = 0

  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName]
    if (!ws || !ws['!ref']) continue
    const range = XLSX.utils.decode_range(ws['!ref'])
    const rows = range.e.r - range.s.r + 1
    const cols = range.e.c - range.s.c + 1
    totalRows += rows
    if (rows * cols > maxCells) {
      maxCells = rows * cols
      maxRows = rows
      maxCols = cols
    }
  }

  return { sheetCount: wb.SheetNames.length, maxRows, maxCols, maxCells, totalRows }
}

// More than this many rows would take too long to draw on a phone.
const MAX_TOTAL_ROWS = 50_000
// Above this width columns get too narrow to read on landscape A4.
const WIDE_TABLE_COLS = 12

export function isTooLarge(c: XlsxClassification): boolean {
  return c.totalRows > MAX_TOTAL_ROWS
}

export function isWideTable(c: XlsxClassification): boolean {
  return c.maxCols > WIDE_TABLE_COLS
}

// ──────── Local render ───────────────────────────────────────────────────────
//
// SheetJS → 2D string matrix → render each PDF page through OffscreenCanvas →
// embed as JPEG in a pdf-lib page. Canvas (rather than pdf-lib drawText) is
// used because the standard pdf-lib fonts are WinAnsi-only and would mangle
// Cyrillic; canvas inherits the system font stack and handles Unicode for free.
//
// Browser requirements: OffscreenCanvas + convertToBlob (Safari 16.4+). On
// older browsers convertExcelToPdf throws and the form shows an error.

const PAGE_W = 842 // A4 landscape (pt)
const PAGE_H = 595
const MARGIN = 30
const RENDER_SCALE = 2

interface CellLayout {
  rows: string[][]
  header: string[] | null
  colCount: number
  colWidths: number[]
}

function measureColumns(
  ctx: OffscreenCanvasRenderingContext2D,
  matrix: string[][],
  innerW: number,
): CellLayout {
  const header = matrix.length > 1 ? matrix[0] ?? [] : null
  const body = matrix.length > 1 ? matrix.slice(1) : matrix
  const colCount = matrix.reduce((m, r) => Math.max(m, r.length), 0)
  if (colCount === 0) {
    return { rows: body, header, colCount: 0, colWidths: [] }
  }

  // Sample max content width per column (cap sampling for very tall sheets).
  const sampleLimit = Math.min(matrix.length, 200)
  const intrinsic = new Array<number>(colCount).fill(0)
  for (let r = 0; r < sampleLimit; r++) {
    const row = matrix[r] ?? []
    for (let c = 0; c < colCount; c++) {
      const text = row[c] ?? ''
      if (!text) continue
      const w = ctx.measureText(text).width
      if (w > (intrinsic[c] ?? 0)) intrinsic[c] = w
    }
  }

  // Clamp each column to [40, 220] pt, then scale to fit innerW.
  const clamped = intrinsic.map((w) => Math.max(40, Math.min(220, w + 12)))
  const total = clamped.reduce((s, w) => s + w, 0)
  const scale = total > 0 ? innerW / total : 1
  const colWidths = clamped.map((w) => w * scale)

  return { rows: body, header, colCount, colWidths }
}

function truncate(
  ctx: OffscreenCanvasRenderingContext2D,
  s: string,
  maxWidth: number,
): string {
  if (!s) return ''
  if (ctx.measureText(s).width <= maxWidth) return s
  let lo = 0
  let hi = s.length
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (ctx.measureText(s.slice(0, mid) + '…').width <= maxWidth) lo = mid
    else hi = mid - 1
  }
  return lo > 0 ? s.slice(0, lo) + '…' : ''
}

export async function convertExcelToPdf(
  file: File,
  signal: AbortSignal,
): Promise<Blob> {
  if (signal.aborted) throw new DOMException('aborted', 'AbortError')

  const XLSX = await import('xlsx')
  const { PDFDocument } = await import('pdf-lib')

  const buf = new Uint8Array(await file.arrayBuffer())
  const wb = XLSX.read(buf, { type: 'array' })

  const pdfDoc = await PDFDocument.create()
  pdfDoc.setProducer('pdfy.ru — Excel → PDF (local)')

  const innerW = PAGE_W - MARGIN * 2
  const innerH = PAGE_H - MARGIN * 2

  for (const sheetName of wb.SheetNames) {
    if (signal.aborted) throw new DOMException('aborted', 'AbortError')

    const ws = wb.Sheets[sheetName]
    if (!ws) continue

    const raw = XLSX.utils.sheet_to_json<unknown[]>(ws, {
      header: 1,
      defval: '',
      blankrows: false,
    })
    if (raw.length === 0) continue

    const matrix: string[][] = raw.map((r) =>
      Array.isArray(r) ? r.map((v) => (v == null ? '' : String(v))) : [],
    )

    const probe = new OffscreenCanvas(PAGE_W * RENDER_SCALE, PAGE_H * RENDER_SCALE)
    const probeCtx = probe.getContext('2d')
    if (!probeCtx) throw new Error('OffscreenCanvas 2d unavailable')
    probeCtx.scale(RENDER_SCALE, RENDER_SCALE)
    probeCtx.font = '10px "Helvetica", "Arial", sans-serif'

    const layout = measureColumns(probeCtx, matrix, innerW)
    if (layout.colCount === 0) continue

    const ROW_H = 18
    const HEADER_H = 22
    const TITLE_H = 24
    const headerVisible = layout.header !== null
    const availableForRows = innerH - TITLE_H - (headerVisible ? HEADER_H : 0)
    const rowsPerPage = Math.max(1, Math.floor(availableForRows / ROW_H))
    const pages = Math.max(1, Math.ceil(layout.rows.length / rowsPerPage))

    for (let p = 0; p < pages; p++) {
      if (signal.aborted) throw new DOMException('aborted', 'AbortError')

      const start = p * rowsPerPage
      const end = Math.min(layout.rows.length, start + rowsPerPage)
      const slice = layout.rows.slice(start, end)

      const canvas = new OffscreenCanvas(PAGE_W * RENDER_SCALE, PAGE_H * RENDER_SCALE)
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('OffscreenCanvas 2d unavailable')
      ctx.scale(RENDER_SCALE, RENDER_SCALE)
      ctx.textBaseline = 'top'

      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, PAGE_W, PAGE_H)

      ctx.fillStyle = '#111111'
      ctx.font = 'bold 13px "Helvetica", "Arial", sans-serif'
      const titleText = pages > 1 ? `${sheetName}  ·  ${p + 1} / ${pages}` : sheetName
      ctx.fillText(titleText, MARGIN, MARGIN)

      const headerY = MARGIN + TITLE_H

      if (headerVisible && layout.header) {
        ctx.fillStyle = '#f3f4f6'
        ctx.fillRect(MARGIN, headerY, innerW, HEADER_H)
        ctx.fillStyle = '#111111'
        ctx.font = 'bold 11px "Helvetica", "Arial", sans-serif'
        let x = MARGIN
        for (let c = 0; c < layout.colCount; c++) {
          const w = layout.colWidths[c] ?? 0
          const text = truncate(ctx, layout.header[c] ?? '', w - 8)
          ctx.fillText(text, x + 4, headerY + 6)
          x += w
        }
      }

      const rowsStartY = headerY + (headerVisible ? HEADER_H : 0)

      ctx.font = '10px "Helvetica", "Arial", sans-serif'
      ctx.fillStyle = '#111111'
      ctx.strokeStyle = '#e5e7eb'
      ctx.lineWidth = 0.5

      slice.forEach((row, ri) => {
        const y = rowsStartY + ri * ROW_H
        let x = MARGIN
        for (let c = 0; c < layout.colCount; c++) {
          const w = layout.colWidths[c] ?? 0
          const cell = row[c] ?? ''
          const text = truncate(ctx, cell, w - 8)
          ctx.fillText(text, x + 4, y + 4)
          x += w
        }
        ctx.beginPath()
        ctx.moveTo(MARGIN, y + ROW_H)
        ctx.lineTo(MARGIN + innerW, y + ROW_H)
        ctx.stroke()
      })

      const gridBottom = rowsStartY + slice.length * ROW_H
      let x = MARGIN
      for (let c = 0; c <= layout.colCount; c++) {
        ctx.beginPath()
        ctx.moveTo(x, headerY)
        ctx.lineTo(x, gridBottom)
        ctx.stroke()
        x += layout.colWidths[c] ?? 0
      }
      ctx.beginPath()
      ctx.moveTo(MARGIN, headerY)
      ctx.lineTo(MARGIN + innerW, headerY)
      ctx.stroke()

      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 })
      const imgBytes = new Uint8Array(await blob.arrayBuffer())
      const jpg = await pdfDoc.embedJpg(imgBytes)
      const page = pdfDoc.addPage([PAGE_W, PAGE_H])
      page.drawImage(jpg, { x: 0, y: 0, width: PAGE_W, height: PAGE_H })
    }
  }

  if (pdfDoc.getPageCount() === 0) {
    pdfDoc.addPage([PAGE_W, PAGE_H])
  }

  const out = await pdfDoc.save({ useObjectStreams: true })
  return new Blob([out], { type: 'application/pdf' })
}
